/**
 * GlobalSearch — spotlight-style Cmd+K popup that searches Customers,
 * Tours, Vendors and Locations via GET /api/pollen/search.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Search, User, Compass, Building2, MapPin, Loader2 } from 'lucide-react';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const GROUPS = [
  { key: 'customers', label: 'Customers', icon: User, color: 'text-emerald-600' },
  { key: 'tours', label: 'Tours', icon: Compass, color: 'text-blue-600' },
  { key: 'vendors', label: 'Vendors', icon: Building2, color: 'text-amber-600' },
  { key: 'locations', label: 'Locations', icon: MapPin, color: 'text-rose-600' },
];

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(''); setResults(null); setActiveIdx(0); return; }
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/pollen/search?q=${encodeURIComponent(query)}`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (r.ok) { setResults(await r.json()); setActiveIdx(0); }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Flatten for keyboard nav
  const flat = results
    ? GROUPS.flatMap(g => (results[g.key] || []).map(item => ({ ...item, group: g.key })))
    : [];

  const go = (item) => {
    setOpen(false);
    if (item.group === 'customers') navigate(`/pollen-dots/customer/${item.id}`);
    else if (item.group === 'tours') navigate(`/pollen-dots/tour/${item.id}`);
    else if (item.group === 'vendors') navigate(`/pollen-dots/vendor/${item.id}`);
    else if (item.group === 'locations') navigate(`/pollen-dots/locations?focus=${item.id}`);
  };

  const onInputKey = (e) => {
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); flat[activeIdx] && go(flat[activeIdx]); }
  };

  let runningIdx = -1;

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border bg-white hover:bg-muted text-muted-foreground transition-colors"
        title="Search everything (Ctrl+K)" data-testid="global-search-btn">
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline text-xs">Search</span>
        <kbd className="hidden lg:inline text-[10px] px-1 py-0.5 rounded bg-muted border font-mono">⌘K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 max-w-lg gap-0 overflow-hidden top-[20%] translate-y-0" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Global search</DialogTitle>
          <div className="flex items-center border-b px-3">
            {loading ? <Loader2 className="w-4 h-4 shrink-0 animate-spin text-muted-foreground" /> : <Search className="w-4 h-4 shrink-0 opacity-50" />}
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onInputKey}
              placeholder="Search customers, tours, hotels, guides, places..."
              className="flex h-12 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
              data-testid="global-search-input" />
          </div>
          <div className="max-h-[55vh] overflow-y-auto p-2">
            {!results && !loading && (
              <p className="py-8 text-center text-xs text-muted-foreground">Type at least 2 characters to search everything.</p>
            )}
            {results && flat.length === 0 && !loading && (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="global-search-empty">No matches for “{q.trim()}”</p>
            )}
            {results && GROUPS.map(g => {
              const items = results[g.key] || [];
              if (!items.length) return null;
              const Icon = g.icon;
              return (
                <div key={g.key} className="mb-1">
                  <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.label}</p>
                  {items.map(item => {
                    runningIdx += 1;
                    const idx = runningIdx;
                    return (
                      <button key={`${g.key}-${item.id}`}
                        onClick={() => go({ ...item, group: g.key })}
                        onMouseEnter={() => setActiveIdx(idx)}
                        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left transition-colors ${idx === activeIdx ? 'bg-primary/10' : 'hover:bg-muted'}`}
                        data-testid={`search-result-${g.key}-${item.id}`}>
                        <Icon className={`w-4 h-4 shrink-0 ${g.color}`} />
                        <span className="text-sm font-medium truncate">{item.name}</span>
                        {item.sub && <span className="text-[11px] text-muted-foreground truncate ml-auto">{item.sub}</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalSearch;
